import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Observation, ObservationFormData } from "./observation.type";

type SaveObservationData = ObservationFormData & {
  mode: "create" | "update";
};

const observationRef = (studentId: string) => doc(db, "observations", studentId);

export const ObservationApi = createApi({
  reducerPath: "observationApi",
  tagTypes: ["observations"],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    saveObservation: builder.mutation<{ id: string; message: string }, SaveObservationData>({
      async queryFn({ studentId, content: rawContent, mode }) {
        try {
          const user = auth.currentUser;
          if (!user) return { error: { message: "Aucun utilisateur connecté." } };

          const content = rawContent.trim();
          if (!studentId) return { error: { message: "Élève introuvable." } };
          if (!content) return { error: { message: "Veuillez saisir une observation." } };

          // L'identifiant du document est celui de l'élève : il ne peut donc y avoir
          // qu'une seule observation active pour un même élève.
          const activeObservationRef = observationRef(studentId);
          const activeObservationSnap = await getDoc(activeObservationRef);

          if (mode === "create" && activeObservationSnap.exists()) {
            return { error: { message: "Une observation active existe déjà pour cet élève." } };
          }
          if (mode === "update" && !activeObservationSnap.exists()) {
            return { error: { message: "Aucune observation active à modifier." } };
          }

          const legacyObservationsSnap = await getDocs(query(
            collection(db, "observations"),
            where("studentId", "==", studentId),
          ));
          const batch = writeBatch(db);

          if (mode === "create") {
            batch.set(activeObservationRef, {
              studentId,
              content,
              createdBy: user.uid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          } else {
            batch.update(activeObservationRef, {
              content,
              updatedAt: serverTimestamp(),
            });
          }

          // Nettoie les anciennes observations créées avant l'unicité par élève.
          legacyObservationsSnap.docs
            .filter((observation) => observation.id !== studentId)
            .forEach((observation) => batch.delete(observation.ref));

          await batch.commit();
          return {
            data: {
              id: studentId,
              message: mode === "create" ? "Observation enregistrée." : "Observation modifiée.",
            },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["observations"],
    }),
    deleteObservation: builder.mutation<{ message: string }, string>({
      async queryFn(studentId) {
        try {
          if (!studentId) return { error: { message: "Élève introuvable." } };

          const legacyObservationsSnap = await getDocs(query(
            collection(db, "observations"),
            where("studentId", "==", studentId),
          ));
          const batch = writeBatch(db);
          legacyObservationsSnap.docs.forEach((observation) => batch.delete(observation.ref));

          // Supprime aussi le document actif si son champ studentId est incohérent.
          const activeObservationRef = observationRef(studentId);
          if (!legacyObservationsSnap.docs.some((observation) => observation.id === studentId)) {
            const activeObservationSnap = await getDoc(activeObservationRef);
            if (activeObservationSnap.exists()) batch.delete(activeObservationRef);
          }

          await batch.commit();
          return { data: { message: "Observation supprimée." } };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["observations"],
    }),
    getObservationByStudent: builder.query<Observation | null, string>({
      async queryFn(studentId) {
        try {
          if (!studentId) return { data: null };

          const snapshot = await getDoc(observationRef(studentId));
          return {
            data: snapshot.exists()
              ? ({ id: snapshot.id, ...snapshot.data() } as Observation)
              : null,
          };
        } catch (error) {
          return { error };
        }
      },
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(studentId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        if (!studentId) return;

        let unsubscribe: (() => void) | undefined;
        try {
          await cacheDataLoaded;
          unsubscribe = onSnapshot(observationRef(studentId), (snapshot) => {
            updateCachedData(() => (
              snapshot.exists()
                ? ({ id: snapshot.id, ...snapshot.data() } as Observation)
                : null
            ));
          });
        } catch (error) {
          console.error("Erreur de souscription à l'observation:", error);
        }

        await cacheEntryRemoved;
        unsubscribe?.();
      },
      providesTags: ["observations"],
    }),
  }),
});

export const {
  useDeleteObservationMutation,
  useGetObservationByStudentQuery,
  useSaveObservationMutation,
} = ObservationApi;
